import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import VoicemailIcon from "@mui/icons-material/VoicemailOutlined";
import FaxIcon from "@mui/icons-material/FaxOutlined";
import RecordingIcon from "@mui/icons-material/GraphicEqOutlined";
import { formatUsPhone } from "../utils/contacts";
import { formatWhen } from "../utils/format";

// The voicemail, fax or recording a dialog is about to send somewhere:
// an icon, what it is, and who it is from and when.

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    backgroundColor: theme.palette.action.hover,
  },
  icon: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.tone.brand.main,
    backgroundColor: theme.palette.tone.brand.soft,
    "& svg": {
      fontSize: 19,
    },
  },
  text: {
    minWidth: 0,
  },
  title: {
    fontSize: 14.5,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  detail: {
    fontSize: 12.5,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
}));

const ICONS = {
  fax: <FaxIcon />,
  voicemail: <VoicemailIcon />,
  recording: <RecordingIcon />,
};

// Title and second line, from the fax, voicemail or recording itself.
const describe = (file, kind) => {
  if (!file) return { title: "", detail: "" };
  const when = formatWhen(file.dateTime || file.date);
  if (kind === "fax") {
    const from = String(formatUsPhone(file.from) || "");
    return {
      title: String(file.title || "").trim() || "Fax",
      detail: [from !== "" ? `From ${from}` : "", when].filter(Boolean).join(" · "),
    };
  }
  if (kind === "recording") {
    return { title: String(file.title || "").trim() || "Recording", detail: when };
  }
  const caller = String(file.name || "").trim() || String(formatUsPhone(file.number) || "");
  return {
    title: caller !== "" ? `Voicemail from ${caller}` : "Voicemail",
    detail: when,
  };
};

const AttachmentRow = ({ file, type }) => {
  const { classes } = useStyles();
  const kind = String(type || "").toLowerCase();
  const { title, detail } = describe(file, kind);
  return (
    <div className={classes.root}>
      <span className={classes.icon}>{ICONS[kind] || ICONS.recording}</span>
      <div className={classes.text}>
        <div className={classes.title}>{title}</div>
        {detail && <div className={classes.detail}>{detail}</div>}
      </div>
    </div>
  );
};

AttachmentRow.propTypes = {
  file: PropTypes.object,
  // "Fax", "Voicemail" or "Recording"
  type: PropTypes.string,
};

export default AttachmentRow;
